import '@/pages/dashboard/assets/scss/page/Monetization/components/SelectContext.scss';
import "@/pages/dashboard/assets/scss/page/comparison/components/TableContext.scss";
import "@/pages/dashboard/assets/scss/page/multiTimezone/components/TableContext.scss";

import React, { useEffect, useState, useRef, useMemo } from 'react';
import {getLocale, shallowEqual, useDispatch, useSelector} from 'umi';

import { SortBy } from 'tea-component/es/table/addons';
import moment from "moment";
import MetricsSettings from "@/pages/dashboard/components/MetricsSettings";
import { useLocalText, useMetricOptions } from "@/pages/dashboard/pages/comparison/_config/hooks";
import { getAggregationObj, getAllRecords } from "@/pages/dashboard/pages/comparison/_config/utils";
import { dailyTimezoneTextLocalConfig, textLocalConfig } from "@/pages/dashboard/pages/multiTimezone/_config/constant";
import { defaultValue } from "@/pages/dashboard/pages/comparison/components/ShowMoreStatistics";
import SelectShowValueType from "@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType";
import PerformanceMetricsTable, { PagingQuery } from "@/pages/dashboard/components/PerformanceMetricsTable";
import {LANGUAGE_MAP} from "@/pages/dashboard/_config/constant";
import { formatAccumulation, formatFilterValues, generateCopyData } from '@/pages/dashboard/utils/utils';
import { downLoadDailyByTimezonePerformanceTable } from "@/pages/dashboard/services/multiTimezone";
import useDailyByTimezoneTableModel from '@/pages/dashboard/servicesModels/multiTimezone/dailyByTimezoneTableModel';
import { getArrObj, useFunctionWithIntl, getAccumulateOptionsWithUTC } from "@/pages/dashboard/pages/multiTimezone/_config/utils";
import DimensionAccumulateRow from '@/pages/dashboard/components/DimensionAccumulateRow';
import { useSelectArrObj } from '@/pages/dashboard/utils/customHook';

export default function TableContext(props: any) {
  const { formValue, metrics: propsMetrics, explainInfo, gameCode, timezoneType, filterLoading } = props;
  const { selector } = useSelector((state: any) => state?.dashboardSetting ?? {}, shallowEqual);
  const localText = useLocalText([...textLocalConfig, ...dailyTimezoneTextLocalConfig]);
  const timezoneTypeRef = useRef(timezoneType);
  const [accumulated, setAccumulated] = useState<string[]>(['date', timezoneType]);
  const accumulatedRef = useRef(['date', timezoneType]);
  const accumulatedCloseRef = useRef(['date', timezoneType]);

  // value only 切换
  const [segmentValue, setSelectType] = useState("value_only");


  // ShowMoreStatistics 下拉多选 相关
  const [showValue, setShowValue] = useState(defaultValue);
  // 表格请求相关
  const [tableData, setTableData] = useState({});
  const [total, setTotal] = useState(0);
  // 排序值
  const [sortValue, setSortValue] = useState<SortBy[]>([]);
  // 分页
  const [tablePaging, setTablePaging] = useState<PagingQuery>({ pageIndex: 1, pageSize: 10 });
  // 列定义相关
  const [columns, setColumns] = useState([]);
  const [copyTableData, setCopyTableData] = useState(null);


  // Accumulate 下拉选的选项
  let SEPARATED_OPTIONS = useSelectArrObj(formValue);

  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
  const options = useMemo(() => getAccumulateOptionsWithUTC(SEPARATED_OPTIONS, timezoneType, `(${localText.timezone})`), [timezoneType, getLocale(), formValue]);

  useEffect(() => {
    if (timezoneTypeRef.current !== timezoneType) {
      setAccumulated(['date', timezoneType]);
      accumulatedRef.current = ['date', timezoneType];
      setSelectType("value_only");
      setShowValue(defaultValue);
      timezoneTypeRef.current = timezoneType;
    }
    setTablePaging({ pageIndex: 1, pageSize: 10 });
    setSortValue([]);
    setTotal(0);
    setColumns([]);
  }, [timezoneType, formValue]);

  const hasDate = accumulated?.includes('date');
  const isAccumulated = !hasDate;
  const metrics = useMemo(() => {
    // Accumulated聚合指标特殊处理 TRU隐藏 （DAU、WAU、MAU加Avg.）
    const formatMetrics = propsMetrics?.filter((v: { value: string; }) => v?.value !== 'total_users')?.map((item: any) => item);
    const newMetrics = isAccumulated ? formatMetrics : propsMetrics;
    return newMetrics;
  }, [propsMetrics, isAccumulated]);

  // Metrics 筛选器相关
  const { metricsValue, setMetricsValue, allMetricOptions, oldMetrics } = useMetricOptions(metrics);
  const metricsObj = getArrObj(metrics);

  const {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns
  } = useFunctionWithIntl(localText, timezoneType, gameCode, selector, isAccumulated);

  const getColumns = (newAccumulated?: undefined) => {
    const removeMetrics = metricsValue.filter((item: any) => oldMetrics.includes(item));
    setColumns([
      DATE_COLUMN,
      ...getAccumulateColumns(newAccumulated || accumulated),
      ...getMetricColumns(removeMetrics, metricsObj, explainInfo, segmentValue)
    ]);
  };

  const dispatch = useDispatch();
  const dailyByTimezoneTableData = useDailyByTimezoneTableModel(props, formValue, dispatch, filterLoading, accumulatedRef?.current, tablePaging, sortValue, segmentValue, timezoneType);
  const {
    componentData,
    error,
    loading,
    getData,
    overLimitBarTriggerKey,
  } = dailyByTimezoneTableData;

  useEffect(() => {
    const { aggregation, metrics_list = [], row_count } = componentData || {};
    const tableData = { metricsList: metrics_list || [] };
    if (aggregation && aggregation.length > 0) {
      tableData.aggregationObj = getAggregationObj(aggregation, localText);
    };
    const copyData = generateCopyData(getAllRecords(tableData, showValue), {gameCode, selectorList: selector});

    // 为了给周六周日行添加高亮. 后期需优化
    tableData?.metricsList?.forEach?.((metric: any) => {
      metric.isSatOrSun = (moment(metric?.date).format('E') === '6' || moment(metric?.date).format('E') === '7');
    });
    setCopyTableData(copyData);
    setTableData(tableData);
    setTotal(row_count);
    getColumns();
  }, [componentData, segmentValue, metricsValue, getLocale()]);

  const onSortChange = (sorts: SortBy[] = []) => {
    setSortValue(sorts);
    getData(formValue, accumulated, tablePaging, sorts, segmentValue);
  };

  const onPagingChange = (pagingQuery: PagingQuery) => {
    setTablePaging(pagingQuery);
    getData(formValue, accumulated, pagingQuery, sortValue, segmentValue);
  };

  return (
    <PerformanceMetricsTable
      isShowProgressBar
      timeType='Day'
      isHighlightSatSunDay
      title={localText?.tableTitle}
      topTip={{
        left: (<div className="overall-conext-bottom">
          <DimensionAccumulateRow
            value={accumulated}
            options={options}
            onChange={(value: string[] | ((prevState: string[]) => string[])) => {
              setAccumulated(value);
              const flag = accumulatedCloseRef.current?.toString() !== value.toString();
              if (flag) {
                accumulatedRef.current = value;
                accumulatedCloseRef.current = value;
                setTablePaging({ pageIndex: 1, pageSize: 10 });
                setSortValue([]);
              }
            }}
          />
          <SelectShowValueType
            timeType={'Day'}
            value={segmentValue}
            onChange={(value) => { 
              setSelectType(value);
            }}
            isShowDod
            isShowSdlw
          />
        </div>),
        right: (<MetricsSettings
          onConfirm={({ metricValues, statisticsType }) => {
            setShowValue(statisticsType);
            setMetricsValue(metricValues);
          }}
          // disabledOptions={[{ value: "date", text: useIntl().formatMessage({ id: "common_date" }) }]}
          metricOptions={allMetricOptions}
          metricsValue={metricsValue}
        />)
      }}
      copyConfig={
        {
          copyData: copyTableData,
          isHaveWeek: accumulated?.includes('date')
        }
      }
      columns={columns}
      records={getAllRecords(tableData, showValue)}
      selectType={segmentValue}
      loading={loading || filterLoading}
      isError={error}
      downloadConfig={{
        onDownload: ({isDownloadStatistics, metrics, dataType, fileFormat}) => {

          const accumulation = formatAccumulation(accumulated);
          const desc = sortValue[0]?.order === 'desc' || (!sortValue[0]);
          const sorted_by = sortValue?.[0]?.by?.replace(segmentValue, '') || 'date';
          const { rangeTime, timeType, diff, ...filters } = formValue;
          const params = {
            game_code: gameCode,
            start_time: formValue.rangeTime[0],
            end_time: formValue.rangeTime[1],
            period: 'daily',
            filters: formatFilterValues(filters),
            accumulation,
            desc,
            sorted_by,
            page: tablePaging?.pageIndex,
            page_size: tablePaging?.pageSize,
            timezone_type: timezoneType,
            metrics,
            show_dod: dataType === "show_dod",
            show_sdlw: dataType === "show_sdlw",
            file_format: fileFormat,
            aggregates: isDownloadStatistics ? showValue : []
          };

          return downLoadDailyByTimezonePerformanceTable(params, LANGUAGE_MAP[getLocale()]);
        },
        timeType: 'Day',
        metricOptions: allMetricOptions,
        showData: { valueOnly: true, showDod: true, showSdlw: true }
      }}
      sorting={
        {
          defaultSorts: sortValue,
          onSortChange
        }
      }
      paging={{
        pageIndex: tablePaging.pageIndex,
        pageSize: tablePaging.pageSize,
        recordCount: total,
        onPagingChange
      }}
      onResizeEnd={(columns) => setColumns(columns)}
      frontLogicalFlag={componentData?.front_logical_flag}
      overLimitBarTriggerKey={overLimitBarTriggerKey}
    />
  );
}
